
import React, { useEffect, useState } from 'react';
import { apiClient } from '@/api/apiClient';
import { useNavigate } from 'react-router-dom';

const Stats = () => {
    const navigate = useNavigate();
    const [stats, setStats] = useState(null);
    const [topProducts, setTopProducts] = useState({ byViews: [], byLikes: [], loaded: false });
    const [topBrands, setTopBrands] = useState([]);
    const [categoriesStats, setCategoriesStats] = useState([]);
    const [engagementStats, setEngagementStats] = useState(null);
    const [activityStats, setActivityStats] = useState([]);
    const [hourlyStats30d, setHourlyStats30d] = useState([]);
    const [hourlyStats7d, setHourlyStats7d] = useState([]);
    const [pricingStats, setPricingStats] = useState(null);
    const [deviceStats, setDeviceStats] = useState([]);
    const [searchStats, setSearchStats] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('overview');
    const [period, setPeriod] = useState('all'); // 'all', '30d', '7d'

    const [timeStats, setTimeStats] = useState(null);
    const [unusedProducts, setUnusedProducts] = useState(null);
    const [dailyVisitors, setDailyVisitors] = useState([]);
    const [dailySignups, setDailySignups] = useState([]);
    const [retentionStats, setRetentionStats] = useState(null);
    const [clickStats, setClickStats] = useState(null);
    const [shopperStats, setShopperStats] = useState(null);
    const [sessionMetrics, setSessionMetrics] = useState(null);

    // Period Toggle Component
    const PeriodToggle = () => (
        <div className="flex space-x-2 mb-6">
            {[
                { id: 'all', label: 'Depuis toujours' },
                { id: '30d', label: '30 derniers jours' },
                { id: '7d', label: '7 derniers jours' }
            ].map(p => (
                <button
                    key={p.id}
                    onClick={() => { setPeriod(p.id); setTopProducts({ byViews: [], byLikes: [], loaded: false }); setTopBrands([]); setClickStats(null); setSessionMetrics(null); }}
                    className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${period === p.id
                        ? 'bg-amber-600 text-white shadow-sm'
                        : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                        }`}
                >
                    {p.label}
                </button>
            ))}
        </div>
    );

    // Initial Overview Fetch
    useEffect(() => {
        const fetchOverview = async () => {
            try {
                const [globalStats, engageStats, actStats, sessionMet, unusedSt] = await Promise.all([
                    apiClient.admin.getStats(),
                    apiClient.admin.getInteractionStats(),
                    apiClient.admin.getActivityGraph(),
                    apiClient.admin.getSessionMetrics('all'),
                    apiClient.admin.getUnusedProducts()
                ]);
                setStats(globalStats);
                setEngagementStats(engageStats);
                setActivityStats(actStats);
                setSessionMetrics(sessionMet);
                setUnusedProducts(unusedSt);
            } catch (err) {
                // console.error("Overview load error", err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };
        fetchOverview();
    }, []);

    // Lazy load other tabs
    useEffect(() => {
        const loadTab = async () => {
            if (activeTab === 'products' && !topProducts.loaded) {
                const data = await apiClient.admin.getTopProducts(period);
                setTopProducts({ ...data, loaded: true });
            }
            if (activeTab === 'brands' && !topBrands.length) {
                const data = await apiClient.admin.getTopBrands(period);
                setTopBrands(data);
            }
            if (activeTab === 'categories' && !categoriesStats.length) {
                const data = await apiClient.admin.getCategoriesStats();
                setCategoriesStats(data);
            }
            if (activeTab === 'search' && !searchStats.length) {
                const data = await apiClient.admin.getTopSearches();
                setSearchStats(data);
            }
            if ((activeTab === 'activity' || activeTab === 'overview') && (!hourlyStats30d.length || !hourlyStats7d.length)) {
                const [h30, h7] = await Promise.all([
                    apiClient.admin.getHourlyActivity('30d'),
                    apiClient.admin.getHourlyActivity('7d')
                ]);
                setHourlyStats30d(h30);
                setHourlyStats7d(h7);
            }
            if (activeTab === 'preferences' && !pricingStats) {
                const data = await apiClient.admin.getPricingStats();
                setPricingStats(data);
            }
            if (activeTab === 'devices' && !deviceStats.length) {
                const data = await apiClient.admin.getDeviceStats();
                setDeviceStats(data);
            }
            if (activeTab === 'audience') {
                if (!dailyVisitors.length && !dailySignups.length) {
                    try {
                        const [visitors, signups] = await Promise.all([
                            apiClient.admin.getDailyVisitors(),
                            apiClient.admin.getDailySignups(),
                        ]);
                        setDailyVisitors(visitors || []);
                        setDailySignups(signups || []);
                    } catch (err) {
                        console.error("Failed to fetch audience basics:", err);
                    }
                }
                if (!retentionStats) {
                    try {
                        const retention = await apiClient.admin.getRetentionStats();
                        setRetentionStats(retention || { error: true });
                    } catch (err) {
                        console.error("Failed to fetch retention stats:", err);
                        setRetentionStats({ error: true });
                    }
                }
                if (!sessionMetrics || sessionMetrics._period !== period) {
                    try {
                        const metrics = await apiClient.admin.getSessionMetrics(period);
                        setSessionMetrics({ ...metrics, _period: period });
                    } catch (err) {
                        console.error("Failed to fetch session metrics:", err);
                        setSessionMetrics({ error: true, _period: period });
                    }
                }
            }
            if (activeTab === 'clicks') {
                if (!clickStats) {
                    try {
                        const data = await apiClient.admin.getTopClicks(period);
                        setClickStats(data);
                    } catch (err) {
                        console.error('Failed to fetch click stats:', err);
                    }
                }
            }
            if (activeTab === 'shopper') {
                if (!shopperStats) {
                    try {
                        const data = await apiClient.admin.getShopperStats();
                        setShopperStats(data);
                    } catch (err) {
                        console.error('Failed to fetch shopper stats:', err);
                    }
                }
            }
        };
        if (!loading) loadTab();
    }, [activeTab, loading, period]);

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="text-xl font-bold text-gray-500 animate-pulse">Chargement des statistiques...</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50 p-8">
                <div className="text-xl font-bold text-red-500 bg-red-50 p-6 rounded-xl border border-red-200">
                    Une erreur est survenue : {error}
                </div>
            </div>
        );
    }

    const renderAudience = () => {
        const totalVisitorsLast30 = dailyVisitors?.reduce((acc, curr) => acc + curr.count, 0) || 0;
        const avgVisitors = dailyVisitors?.length > 0 ? (totalVisitorsLast30 / dailyVisitors.length).toFixed(1) : 0;

        const totalSignupsLast30 = dailySignups?.reduce((acc, curr) => acc + curr.count, 0) || 0;
        const avgSignups = dailySignups?.length > 0 ? (totalSignupsLast30 / dailySignups.length).toFixed(1) : 0;

        return (
            <div className="space-y-8">
                {/* Visual Dashboard Stats */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100 flex flex-col justify-center">
                        <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Visiteurs Mensuels (30j)</div>
                        <div className="text-3xl font-black text-orange-600 mt-1">{totalVisitorsLast30}</div>
                        <div className="text-xs text-gray-500 mt-1">Moyenne: <span className="font-bold">{avgVisitors}</span> / jour</div>
                    </div>
                    <div className="bg-white p-5 rounded-xl shadow-sm border border-gray-100 flex flex-col justify-center">
                        <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Inscriptions Mensuelles (30j)</div>
                        <div className="text-3xl font-black text-amber-600 mt-1">{totalSignupsLast30}</div>
                        <div className="text-xs text-gray-500 mt-1">Moyenne: <span className="font-bold">{avgSignups}</span> / jour</div>
                    </div>
                </div>

                {/* Retention Stats */}
                {retentionStats && retentionStats.error && (
                    <div className="bg-red-50 p-6 rounded-xl shadow-sm border border-red-100 text-red-600 font-medium">
                        Impossible de charger les statistiques de rétention. Veuillez vérifier la console ou relancer le backend.
                    </div>
                )}
                {retentionStats && !retentionStats.error && retentionStats.visitor && retentionStats.registration && (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Visitor Retention */}
                        <div className="bg-white p-6 rounded-xl shadow-sm border border-orange-100">
                            <h3 className="text-sm font-bold text-gray-800 uppercase tracking-widest mb-4 flex items-center gap-2">
                                <span className="text-xl">🔁</span>
                                Rétention Visiteurs (Retour global)
                            </h3>
                            <div className="grid grid-cols-3 gap-2 text-center divide-x divide-gray-100">
                                <div>
                                    <div className="text-2xl font-black text-orange-500">{retentionStats.visitor?.d1}%</div>
                                    <div className="text-xs text-gray-500 font-medium">1 Jour</div>
                                </div>
                                <div>
                                    <div className="text-2xl font-black text-orange-500">{retentionStats.visitor?.d7}%</div>
                                    <div className="text-xs text-gray-500 font-medium">7 Jours</div>
                                </div>
                                <div>
                                    <div className="text-2xl font-black text-orange-500">{retentionStats.visitor?.d30}%</div>
                                    <div className="text-xs text-gray-500 font-medium">30 Jours</div>
                                </div>
                            </div>
                            <p className="text-[10px] text-gray-400 text-center mt-3">Taux de retour depuis la première visite (statistique globale visiteur).</p>
                        </div>

                        {/* Registered Retention */}
                        <div className="bg-white p-6 rounded-xl shadow-sm border border-amber-100">
                            <h3 className="text-sm font-bold text-gray-800 uppercase tracking-widest mb-4 flex items-center gap-2">
                                <span className="text-xl">🎯</span>
                                Rétention Inscrits (Fidélité)
                            </h3>
                            <div className="grid grid-cols-3 gap-2 text-center divide-x divide-gray-100">
                                <div>
                                    <div className="text-2xl font-black text-amber-500">{retentionStats.registration?.d1}%</div>
                                    <div className="text-xs text-gray-500 font-medium">1 Jour</div>
                                </div>
                                <div>
                                    <div className="text-2xl font-black text-amber-500">{retentionStats.registration?.d7}%</div>
                                    <div className="text-xs text-gray-500 font-medium">7 Jours</div>
                                </div>
                                <div>
                                    <div className="text-2xl font-black text-amber-500">{retentionStats.registration?.d30}%</div>
                                    <div className="text-xs text-gray-500 font-medium">30 Jours</div>
                                </div>
                            </div>
                            <p className="text-[10px] text-gray-400 text-center mt-3">Taux de retour après la création d'un compte (fidélisation des inscrits).</p>
                        </div>
                    </div>
                )}

                {/* Temps de Rétention (Accroche) */}
                {sessionMetrics && !sessionMetrics.error && (
                    <div className="bg-white p-6 rounded-xl shadow-sm border border-amber-100">
                        <div className="flex justify-between items-center mb-6">
                            <h3 className="text-sm font-bold text-gray-800 uppercase tracking-widest flex items-center gap-2">
                                <span className="text-xl">⏱️</span>
                                Temps de Rétention (Accroche)
                            </h3>
                            <div className="text-xs font-medium text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                                {sessionMetrics.totalSessions} sessions analysées
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                            <div className="bg-amber-50/50 p-4 rounded-lg border border-amber-50 flex flex-col items-center justify-center">
                                <div className="text-[10px] font-bold text-amber-500 uppercase tracking-widest mb-1">Temps Moyen</div>
                                <div className="text-3xl font-black text-amber-600">{Math.round(sessionMetrics.averageDuration)}s</div>
                            </div>
                            <div className="bg-amber-50/50 p-4 rounded-lg border border-amber-50 flex flex-col items-center justify-center">
                                <div className="text-[10px] font-bold text-amber-500 uppercase tracking-widest mb-1">Temps Médian</div>
                                <div className="text-3xl font-black text-amber-600">{Math.round(sessionMetrics.medianDuration)}s</div>
                            </div>
                            <div className="bg-amber-50/50 p-4 rounded-lg border border-amber-50 flex flex-col items-center justify-center">
                                <div className="text-[10px] font-bold text-amber-500 uppercase tracking-widest mb-1">Top 20% (Quintile 4)</div>
                                <div className="text-3xl font-black text-amber-600">{Math.round(sessionMetrics.quintiles[3] || 0)}s+</div>
                            </div>
                        </div>

                        <h4 className="text-xs font-bold text-gray-600 uppercase mb-4">Taux de Rebond (Départs avant...)</h4>
                        <div className="flex justify-between items-end h-32 gap-2">
                            {[
                                { label: '< 10s', value: sessionMetrics.proportions.lessThan10s, color: 'bg-red-500', bgHover: 'hover:bg-red-600' },
                                { label: '< 30s', value: sessionMetrics.proportions.lessThan30s, color: 'bg-orange-500', bgHover: 'hover:bg-orange-600' },
                                { label: '< 1m', value: sessionMetrics.proportions.lessThan1m, color: 'bg-yellow-500', bgHover: 'hover:bg-yellow-600' },
                                { label: '< 5m', value: sessionMetrics.proportions.lessThan5m, color: 'bg-green-500', bgHover: 'hover:bg-green-600' },
                                { label: '< 10m', value: sessionMetrics.proportions.lessThan10m, color: 'bg-emerald-500', bgHover: 'hover:bg-emerald-600' },
                            ].map((item, idx) => (
                                <div key={idx} className="flex-1 flex flex-col justify-end items-center group relative">
                                    <div className="text-xs font-bold text-gray-700 mb-2">{item.value}%</div>
                                    <div 
                                        className={`w-full rounded-t-md transition-all cursor-pointer ${item.color} ${item.bgHover} shadow-sm`}
                                        style={{ height: `${Math.max(item.value, 5)}%`, opacity: 0.8 }}
                                    >
                                        <div className="opacity-0 group-hover:opacity-100 absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 bg-gray-900 text-white text-[10px] py-1 px-2 rounded whitespace-nowrap pointer-events-none transition-opacity z-10">
                                            {item.value}% partent avant {item.label.replace('< ', '')}
                                        </div>
                                    </div>
                                    <div className="text-[10px] font-bold text-gray-500 mt-2">{item.label}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Visitor Trend Chart */}
                <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-100">
                    <div className="flex justify-between items-center mb-8">
                        <div>
                            <h3 className="text-lg font-bold text-gray-800 uppercase tracking-tight">🌍 Tendance de l'Audience</h3>
                            <p className="text-sm text-gray-400">Evolution du nombre de visiteurs sur les 30 derniers jours</p>
                        </div>
                    </div>

                    <div className="flex items-end space-x-1 h-56 overflow-x-auto pb-4 custom-scrollbar">
                        {dailyVisitors && dailyVisitors.length > 0 ? dailyVisitors.map((d, idx) => {
                            const maxCount = Math.max(...dailyVisitors.map(v => v.count), 1);
                            const heightPercent = (d.count / maxCount) * 100;
                            const isToday = new Date(d.date).toDateString() === new Date().toDateString();

                            return (
                                <div key={idx} className="flex flex-col justify-end items-center group relative min-w-[24px] flex-1 h-full">
                                    <div
                                        className={`w-full rounded-t transition-all cursor-crosshair relative ${isToday ? 'bg-orange-600' : 'bg-orange-100 group-hover:bg-orange-400'}`}
                                        style={{ height: `${Math.max(heightPercent, 2)}%` }}
                                    >
                                        <div className="opacity-0 group-hover:opacity-100 absolute bottom-full left-1/2 transform -translate-x-1/2 mb-3 bg-gray-900 text-white text-[10px] py-1.5 px-3 rounded-lg z-20 whitespace-nowrap shadow-xl pointer-events-none transition-opacity">
                                            <div className="font-bold">{new Date(d.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}</div>
                                            <div className="text-orange-400 font-extrabold">{d.count} visiteurs</div>
                                        </div>
                                    </div>
                                    <div className="text-[9px] text-gray-400 mt-3 font-medium rotate-45 origin-left whitespace-nowrap">
                                        {new Date(d.date).getDate()}/{new Date(d.date).getMonth() + 1}
                                    </div>
                                </div>
                            );
                        }) : (
                            <div className="flex-1 flex items-center justify-center text-gray-300 italic">Pas assez de données pour générer le graphique</div>
                        )}
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Daily Visitors Table */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                        <div className="p-6 border-b border-gray-100 bg-gray-50/50">
                            <h3 className="text-sm font-bold text-gray-800 uppercase tracking-widest flex items-center gap-2">
                                <span className="w-2 h-2 rounded-full bg-orange-500 animate-pulse"></span>
                                Historique Détaillé
                            </h3>
                        </div>
                        <div className="overflow-auto max-h-80">
                            <table className="w-full text-left text-xs text-gray-600">
                                <thead className="bg-gray-100/50 text-gray-500 sticky top-0 uppercase font-bold text-[10px]">
                                    <tr>
                                        <th className="p-4 border-b border-gray-100">Date</th>
                                        <th className="p-4 border-b border-gray-100 text-right">Visiteurs</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {dailyVisitors && dailyVisitors.length > 0 ? dailyVisitors.slice().reverse().map((d, idx) => (
                                        <tr key={idx} className="hover:bg-orange-50/50 transition-colors">
                                            <td className="p-3 font-medium text-gray-900 dark:text-white">{new Date(d.date).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'long' })}</td>
                                            <td className="p-3 text-right font-black text-orange-600">{d.count}</td>
                                        </tr>
                                    )) : (
                                        <tr><td colSpan="2" className="p-8 text-center text-gray-400">Aucune donnée.</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Daily Signups Table */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                        <div className="p-6 border-b border-gray-100 bg-gray-50/50">
                            <h3 className="text-sm font-bold text-gray-800 uppercase tracking-widest flex items-center gap-2">
                                <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse"></span>
                                Inscriptions
                            </h3>
                        </div>
                        <div className="overflow-auto max-h-80">
                            <table className="w-full text-left text-xs text-gray-600">
                                <thead className="bg-gray-100/50 text-gray-500 sticky top-0 uppercase font-bold text-[10px]">
                                    <tr>
                                        <th className="p-4 border-b border-gray-100">Date</th>
                                        <th className="p-4 border-b border-gray-100 text-right">Comptes</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {dailySignups && dailySignups.length > 0 ? dailySignups.slice().reverse().map((d, idx) => (
                                        <tr key={idx} className="hover:bg-amber-50/50 transition-colors">
                                            <td className="p-3 font-medium text-gray-900 dark:text-white">{new Date(d.date).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'long' })}</td>
                                            <td className="p-3 text-right font-black text-amber-600">{d.count}</td>
                                        </tr>
                                    )) : (
                                        <tr><td colSpan="2" className="p-8 text-center text-gray-400">Aucune inscription.</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        );
    };



    const renderOverview = () => (
        <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <StatCard
                    title="PRODUITS"
                    value={stats?.products?.total}
                    subtitle={`${stats?.products?.zeroViews ?? 0} jamais vus`}
                    color="blue"
                />
                <StatCard
                    title="UTILISATEURS"
                    value={stats?.users?.total}
                    subtitle={`Insc: ${stats?.users?.registered ?? 0} | Guest: ${stats?.users?.guests ?? 0}`}
                    color="indigo"
                />
                <StatCard
                    title="VISITEURS"
                    value={stats?.visitors?.total}
                    subtitle="Uniques (IP Hachée)"
                    color="orange"
                />
                <StatCard
                    title="INTERACTIONS"
                    value={stats?.interactions?.total}
                    subtitle={`Taux Like: ${stats?.interactions?.likeRate ?? 0}%`}
                    color="green"
                />
                <StatCard
                    title="TEMPS MOYEN"
                    value={sessionMetrics?.averageDuration ? Math.round(sessionMetrics.averageDuration) + 's' : '-'}
                    subtitle={`Médiane: ${sessionMetrics?.medianDuration ? Math.round(sessionMetrics.medianDuration) : '-'}s`}
                    color="orange"
                />
                <StatCard
                    title="IA SHOPPER"
                    value={stats?.interactions?.shopperSessions || 0}
                    subtitle="Discussions générées"
                    color="pink"
                />
            </div>

            {/* Engagement Graph Placeholder or Small Activity Graph */}
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                <h3 className="text-lg font-bold text-gray-800 mb-4">Activité Récente</h3>
                {activityStats && activityStats.length > 0 ? (
                    <div className="flex items-end space-x-2 h-32 overflow-hidden">
                        {activityStats.slice(-14).map((day, idx) => { // Last 14 days
                            const maxCount = Math.max(...activityStats.map(d => parseInt(d.count)));
                            const heightPercent = maxCount > 0 ? Math.min((parseInt(day.count) / maxCount) * 100, 100) : 0;
                            return (
                                <div key={idx} className="flex flex-col justify-end items-center flex-1 min-w-[10px] h-full">
                                    <div className="w-full bg-amber-500 rounded-t" style={{ height: `${heightPercent}%` }}></div>
                                    <div className="text-[10px] text-gray-400 mt-1">{new Date(day.date).getDate()}</div>
                                </div>
                            );
                        })}
                    </div>
                ) : <p className="text-gray-400 text-sm">Chargement du graphe...</p>}
            </div>

            {/* Unused Products Alert */}
            {unusedProducts?.count > 0 && (
                <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-r shadow-sm">
                    <h3 className="font-bold text-red-700">⚠️ Produits Ignorés</h3>
                    <p className="text-sm text-red-600">il y a {unusedProducts.count} produits qui n'ont jamais été vus par personne.</p>
                </div>
            )}

            {/* ... Existing top lists ... */}
        </div>
    );

    const renderProducts = () => (
        <div className="space-y-8">
            <PeriodToggle />
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="p-6 border-b border-gray-100">
                    <h3 className="text-lg font-bold text-gray-800">Top Produits ({period === 'all' ? 'Toujours' : period})</h3>
                    <p className="text-sm text-gray-500">Classé par nombre de vues. Le CTR indique l'attractivité (Clics vers site / Vues).</p>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm text-gray-600">
                        <thead className="bg-gray-50 text-xs uppercase font-semibold text-gray-500">
                            <tr>
                                <th className="p-4">Produit</th>
                                <th className="p-4">Marque</th>
                                <th className="p-4">Prix</th>
                                <th className="p-4 text-right">Vues</th>
                                <th className="p-4 text-right">Clics Site</th>
                                <th className="p-4 text-right">CTR</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {topProducts.byViews.map((p, i) => {
                                const ctr = Number(p.view_count) > 0 ? (Number(p.click_count || 0) / Number(p.view_count) * 100).toFixed(1) : '0.0';
                                return (
                                    <tr key={p.id} className="hover:bg-gray-50">
                                        <td className="p-4 font-medium text-gray-900 dark:text-white max-w-xs truncate" title={p.name}>{p.name}</td>
                                        <td className="p-4">{p.brand}</td>
                                        <td className="p-4">{p.price ? `${p.price} €` : '-'}</td>
                                        <td className="p-4 text-right font-bold text-amber-600">{p.view_count}</td>
                                        <td className="p-4 text-right font-bold text-green-600">{p.click_count || 0}</td>
                                        <td className={`p-4 text-right font-bold ${ctr > 5 ? 'text-green-500' : 'text-gray-500'}`}>{ctr}%</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="p-6 border-b border-gray-100">
                    <h3 className="text-lg font-bold text-gray-800">Top Produits par Likes</h3>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm text-gray-600">
                        <thead className="bg-gray-50 text-xs uppercase font-semibold text-gray-500">
                            <tr>
                                <th className="p-4">Produit</th>
                                <th className="p-4">Marque</th>
                                <th className="p-4">Prix</th>
                                <th className="p-4 text-right">Likes</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {topProducts.byLikes.map((p, i) => (
                                <tr key={p.id} className="hover:bg-gray-50">
                                    <td className="p-4 font-medium text-gray-900 dark:text-white max-w-xs truncate" title={p.name}>{p.name}</td>
                                    <td className="p-4">{p.brand}</td>
                                    <td className="p-4">{p.price ? `${p.price} €` : '-'}</td>
                                    <td className="p-4 text-right font-bold text-amber-600">{p.like_count}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );

    const renderBrands = () => (
        <div className="space-y-6">
            <PeriodToggle />
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="p-6 border-b border-gray-100">
                    <h3 className="text-lg font-bold text-gray-800">Performance des Marques ({period === 'all' ? 'Toujours' : period})</h3>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm text-gray-600">
                        <thead className="bg-gray-50 text-xs uppercase font-semibold text-gray-500">
                            <tr>
                                <th className="p-4">Marque</th>
                                <th className="p-4 text-right">Interactions Totales</th>
                                <th className="p-4 text-right">Likes</th>
                                <th className="p-4 text-right">Taux de Like Approx.</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {topBrands.map((b, i) => {
                                const likeRate = b.interaction_count > 0 ? (b.like_count / b.interaction_count * 100).toFixed(1) : 0;
                                return (
                                    <tr key={i} className="hover:bg-gray-50">
                                        <td className="p-4 font-bold text-gray-900 dark:text-white">{b.brand_name}</td>
                                        <td className="p-4 text-right">{b.interaction_count}</td>
                                        <td className="p-4 text-right text-green-600">{b.like_count}</td>
                                        <td className="p-4 text-right text-gray-500">{likeRate}%</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );

    const renderEngagement = () => (
        <div className="space-y-6">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <h3 className="text-lg font-bold text-gray-800 mb-6">Analyse des Swipes</h3>
                <div className="flex justify-center items-center space-x-12 p-4">
                    {engagementStats && engagementStats.swipes && engagementStats.swipes.ratio.map((item) => (
                        <div key={item.action} className="text-center">
                            <div className={`text-5xl font-black ${['like', 'swipe_right'].includes(item.action) ? 'text-green-500' : 'text-red-500'}`}>
                                {item.percentage}%
                            </div>
                            <div className="text-sm text-gray-500 uppercase tracking-widest mt-2">{item.action}</div>
                            <div className="text-xs text-gray-400 mt-1">({item.count} total)</div>
                        </div>
                    ))}
                    {(!engagementStats?.swipes?.total) && (
                        <div className="text-gray-400">Pas assez de données</div>
                    )}
                </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <h3 className="text-lg font-bold text-gray-800 mb-4">Détail des Interactions</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {engagementStats?.breakdown?.map((item) => (
                        <div key={item.action} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                            <span className="capitalize font-medium text-gray-700">{item.action.replace(/_/g, ' ')}</span>
                            <span className="font-mono font-bold text-gray-900 dark:text-white">{item.count}</span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );

    const renderActivity = () => (
        <div className="space-y-8">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                <h3 className="text-lg font-bold text-gray-800 mb-6">Activité Quotidienne (30 derniers jours)</h3>
                <div className="flex items-end space-x-2 h-64 overflow-x-auto pb-2 pr-6 custom-scrollbar">
                    {activityStats && activityStats.map((day, idx) => {
                        const maxCount = Math.max(...activityStats.map(d => parseInt(d.count)));
                        const heightPercent = maxCount > 0 ? Math.min((parseInt(day.count) / maxCount) * 100, 100) : 0;
                        return (
                            <div key={idx} className="flex flex-col justify-end items-center group relative min-w-[20px] flex-1 h-full">
                                <div
                                    className="w-full bg-amber-500 rounded-t hover:bg-amber-600 transition-all relative"
                                    style={{ height: `${heightPercent}%` }}
                                >
                                    <div className="hidden group-hover:block absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 bg-black text-white text-xs py-1 px-2 rounded z-10 whitespace-nowrap">
                                        {new Date(day.date).toLocaleDateString()} : {day.count} actions
                                    </div>
                                </div>
                                <div className="text-[10px] text-gray-400 mt-2 rotate-45 origin-left truncate w-full">
                                    {new Date(day.date).getDate()}/{new Date(day.date).getMonth() + 1}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* 7 Days Peak Hours Table */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                    <div className="p-6 border-b border-gray-100 bg-gray-50/50">
                        <h3 className="text-sm font-bold text-gray-800 uppercase tracking-widest flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-amber-500"></span>
                            Heures de pointe (7 derniers jours)
                        </h3>
                    </div>
                    <div className="overflow-auto max-h-80">
                        <table className="w-full text-left text-sm text-gray-600">
                            <thead className="bg-gray-100/50 text-gray-500 sticky top-0 uppercase font-bold text-[10px]">
                                <tr>
                                    <th className="p-4 border-b border-gray-100">Heure</th>
                                    <th className="p-4 border-b border-gray-100 text-right">Interactions (Volume)</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {hourlyStats7d && hourlyStats7d.slice().sort((a, b) => b.count - a.count).map((h, idx) => (
                                    <tr key={idx} className="hover:bg-amber-50/50 transition-colors">
                                        <td className="p-3 font-medium text-gray-900 dark:text-white">{h.hour}h00 - {h.hour}h59</td>
                                        <td className="p-3 text-right font-black text-amber-600">{h.count}</td>
                                    </tr>
                                ))}
                                {(!hourlyStats7d || hourlyStats7d.length === 0) && (
                                    <tr><td colSpan="2" className="p-8 text-center text-gray-400">Aucune donnée.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* 30 Days Peak Hours Table */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                    <div className="p-6 border-b border-gray-100 bg-gray-50/50">
                        <h3 className="text-sm font-bold text-gray-800 uppercase tracking-widest flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-amber-600"></span>
                            Heures de pointe (30 derniers jours)
                        </h3>
                    </div>
                    <div className="overflow-auto max-h-80">
                        <table className="w-full text-left text-sm text-gray-600">
                            <thead className="bg-gray-100/50 text-gray-500 sticky top-0 uppercase font-bold text-[10px]">
                                <tr>
                                    <th className="p-4 border-b border-gray-100">Heure</th>
                                    <th className="p-4 border-b border-gray-100 text-right">Interactions (Volume)</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {hourlyStats30d && hourlyStats30d.slice().sort((a, b) => b.count - a.count).map((h, idx) => (
                                    <tr key={idx} className="hover:bg-amber-50/50 transition-colors">
                                        <td className="p-3 font-medium text-gray-900 dark:text-white">{h.hour}h00 - {h.hour}h59</td>
                                        <td className="p-3 text-right font-black text-amber-600">{h.count}</td>
                                    </tr>
                                ))}
                                {(!hourlyStats30d || hourlyStats30d.length === 0) && (
                                    <tr><td colSpan="2" className="p-8 text-center text-gray-400">Aucune donnée.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderCategories = () => (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="p-6 border-b border-gray-100">
                <h3 className="text-lg font-bold text-gray-800">Collections Utilisateurs Populaires</h3>
            </div>
            <div className="overflow-x-auto">
                <table className="w-full text-left text-sm text-gray-600">
                    <thead className="bg-gray-50 text-xs uppercase font-semibold text-gray-500">
                        <tr>
                            <th className="p-4">Nom de la Collection</th>
                            <th className="p-4 text-right">Items Ajoutés</th>
                            <th className="p-4 text-right">Utilisateurs Uniques</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                        {categoriesStats && categoriesStats.map((cat, idx) => (
                            <tr key={idx} className="hover:bg-gray-50">
                                <td className="p-4 font-bold text-gray-900 dark:text-white">{cat.category_name}</td>
                                <td className="p-4 text-right text-amber-600 font-bold">{cat.usage_count}</td>
                                <td className="p-4 text-right">{cat.unique_users}</td>
                            </tr>
                        ))}
                        {categoriesStats && categoriesStats.length === 0 && (
                            <tr><td colSpan="3" className="p-8 text-center text-gray-400">Aucune collection créée pour le moment.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );



    const renderDevices = () => {
        const totalTraffic = deviceStats ? deviceStats.reduce((acc, curr) => acc + curr.share, 0) : 0;

        return (
            <div className="space-y-6">
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                    <h3 className="text-lg font-bold text-gray-800 mb-6">Répartition par Support (Mobile vs Desktop)</h3>
                    <div className="flex flex-col md:flex-row items-center justify-around gap-8">
                        {deviceStats && deviceStats.map((d, idx) => {
                            const percent = totalTraffic > 0 ? Math.round((d.share / totalTraffic) * 100) : 0;
                            const color = d.device === 'Mobile' ? 'text-amber-600' : d.device === 'Desktop' ? 'text-amber-700' : 'text-gray-400';

                            return (
                                <div key={idx} className="flex flex-col items-center p-6 rounded-lg bg-gray-50 flex-1 w-full relative overflow-hidden">
                                    <div className={`absolute top-0 right-0 p-4 opacity-10 font-black text-6xl uppercase ${color}`}>
                                        {d.device === 'Mobile' ? '📱' : d.device === 'Desktop' ? '💻' : '?'}
                                    </div>
                                    <div className={`text-4xl font-bold ${color} mb-2 z-10`}>{percent}%</div>
                                    <div className="text-gray-600 font-medium z-10">{d.device}</div>
                                    <div className="text-xs text-gray-400 mt-1 z-10">({d.share} interactions)</div>

                                    <div className="mt-6 w-full bg-white rounded-lg p-3 shadow-sm z-10">
                                        <div className="text-xs text-gray-500 uppercase tracking-widest mb-1">CTR (Attractivité)</div>
                                        <div className={`text-xl font-bold ${parseFloat(d.ctr) >= 1 ? 'text-green-600' : 'text-gray-600'}`}>
                                            {d.ctr}%
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                        {(!deviceStats || deviceStats.length === 0) && (
                            <div className="text-gray-400">Pas assez de données pour l'analyse des supports.</div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    const renderClicks = () => (
        <div className="space-y-8">
            <PeriodToggle />

            {/* Total Clicks KPI */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <StatCard
                    title="CLICS TOTAUX"
                    value={clickStats?.totalClicks ?? '-'}
                    subtitle="Redirections vers les sites"
                    color="green"
                />
                <StatCard
                    title="PRODUITS CLIQUÉS"
                    value={clickStats?.byProducts?.length ?? '-'}
                    subtitle="Produits distincts"
                    color="blue"
                />
                <StatCard
                    title="MARQUES CLIQUÉES"
                    value={clickStats?.byBrands?.length ?? '-'}
                    subtitle="Marques distinctes"
                    color="indigo"
                />
            </div>

            {/* Top Products by Clicks */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="p-6 border-b border-gray-100 bg-gray-50/50">
                    <h3 className="text-sm font-bold text-gray-800 uppercase tracking-widest flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-green-500"></span>
                        Top Produits par Clics vers le site ({period === 'all' ? 'Toujours' : period})
                    </h3>
                    <p className="text-xs text-gray-500 mt-1">Produits ayant généré le plus de redirections vers les sites marchands</p>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm text-gray-600">
                        <thead className="bg-gray-100/50 text-gray-500 sticky top-0 uppercase font-bold text-[10px]">
                            <tr>
                                <th className="p-4 border-b border-gray-100">#</th>
                                <th className="p-4 border-b border-gray-100">Produit</th>
                                <th className="p-4 border-b border-gray-100">Marque</th>
                                <th className="p-4 border-b border-gray-100">Prix</th>
                                <th className="p-4 border-b border-gray-100 text-right">Clics</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {clickStats?.byProducts?.length > 0 ? clickStats.byProducts.map((p, idx) => (
                                <tr key={p.id} className="hover:bg-green-50/50 transition-colors">
                                    <td className="p-3 text-gray-400 font-mono text-xs">{idx + 1}</td>
                                    <td className="p-3 font-medium text-gray-900 dark:text-white max-w-xs truncate" title={p.name}>{p.name}</td>
                                    <td className="p-3 text-amber-600 font-medium">{p.brand}</td>
                                    <td className="p-3">{p.price ? `${p.price} €` : '-'}</td>
                                    <td className="p-3 text-right font-black text-green-600">{p.click_count}</td>
                                </tr>
                            )) : (
                                <tr><td colSpan="5" className="p-8 text-center text-gray-400">Aucun clic enregistré pour cette période.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Top Brands by Clicks */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="p-6 border-b border-gray-100 bg-gray-50/50">
                    <h3 className="text-sm font-bold text-gray-800 uppercase tracking-widest flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full bg-amber-500"></span>
                        Top Marques par Clics vers le site ({period === 'all' ? 'Toujours' : period})
                    </h3>
                    <p className="text-xs text-gray-500 mt-1">Marques dont les produits génèrent le plus de trafic sortant</p>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-left text-sm text-gray-600">
                        <thead className="bg-gray-100/50 text-gray-500 sticky top-0 uppercase font-bold text-[10px]">
                            <tr>
                                <th className="p-4 border-b border-gray-100">#</th>
                                <th className="p-4 border-b border-gray-100">Marque</th>
                                <th className="p-4 border-b border-gray-100 text-right">Clics Totaux</th>
                                <th className="p-4 border-b border-gray-100 text-right">Produits Distincts</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {clickStats?.byBrands?.length > 0 ? clickStats.byBrands.map((b, idx) => (
                                <tr key={idx} className="hover:bg-amber-50/50 transition-colors">
                                    <td className="p-3 text-gray-400 font-mono text-xs">{idx + 1}</td>
                                    <td className="p-3 font-bold text-gray-900 dark:text-white">{b.brand_name}</td>
                                    <td className="p-3 text-right font-black text-green-600">{b.click_count}</td>
                                    <td className="p-3 text-right text-gray-600">{b.unique_products_clicked}</td>
                                </tr>
                            )) : (
                                <tr><td colSpan="4" className="p-8 text-center text-gray-400">Aucun clic enregistré pour cette période.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );

    const renderShopper = () => {
        return (
            <div className="space-y-8">
                {/* Header Banner */}
                <div className="bg-gradient-to-r from-amber-600 via-amber-600 to-yellow-600 text-white p-6 rounded-2xl shadow-md flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur flex items-center justify-center text-2xl">
                            🤖
                        </div>
                        <div>
                            <h2 className="text-2xl font-black">L'IA Personal Shopper</h2>
                            <p className="text-amber-100 text-sm">Tableau de bord dédié aux conseils et discussions IA</p>
                        </div>
                    </div>
                </div>

                {/* KPI Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <StatCard
                        title="7 DERNIERS JOURS"
                        value={shopperStats?.total7d ?? '-'}
                        subtitle={`Moy. ${shopperStats?.avg7d ?? 0} / jour`}
                        color="blue"
                    />
                    <StatCard
                        title="3 DERNIERS JOURS"
                        value={shopperStats?.total3d ?? '-'}
                        subtitle={`Moy. ${shopperStats?.avg3d ?? 0} / jour`}
                        color="indigo"
                    />
                    <StatCard
                        title="30 DERNIERS JOURS"
                        value={shopperStats?.total30d ?? '-'}
                        subtitle={`Moy. ${shopperStats?.avg30d ?? 0} / jour`}
                        color="indigo"
                    />
                    <StatCard
                        title="MESSAGES / DISCUSSION"
                        value={shopperStats?.avgMessages ?? '-'}
                        subtitle="Répliques par session"
                        color="green"
                    />
                </div>

                {/* Daily Trend Chart */}
                <div className="bg-white p-8 rounded-xl shadow-sm border border-amber-100 dark:border-amber-900/40">
                    <div className="flex justify-between items-center mb-8">
                        <div>
                            <h3 className="text-lg font-bold text-gray-800 uppercase tracking-tight flex items-center gap-2">
                                <span>💬</span> Tendance des Discussions (30 derniers jours)
                            </h3>
                            <p className="text-sm text-gray-400">Évolution quotidienne du nombre de sessions créées avec Liya</p>
                        </div>
                    </div>

                    <div className="flex items-end space-x-1 h-56 overflow-x-auto pb-4 custom-scrollbar">
                        {shopperStats?.daily30d && shopperStats.daily30d.length > 0 ? shopperStats.daily30d.map((d, idx) => {
                            const maxCount = Math.max(...shopperStats.daily30d.map(v => parseInt(v.count)), 1);
                            const heightPercent = (parseInt(d.count) / maxCount) * 100;
                            const isToday = new Date(d.date).toDateString() === new Date().toDateString();

                            return (
                                <div key={idx} className="flex flex-col justify-end items-center group relative min-w-[24px] flex-1 h-full">
                                    <div
                                        className={`w-full rounded-t transition-all cursor-crosshair relative ${isToday ? 'bg-amber-600' : 'bg-amber-300 group-hover:bg-amber-500'}`}
                                        style={{ height: `${Math.max(heightPercent, 4)}%` }}
                                    >
                                        <div className="opacity-0 group-hover:opacity-100 absolute bottom-full left-1/2 transform -translate-x-1/2 mb-3 bg-gray-900 text-white text-[10px] py-1.5 px-3 rounded-lg z-20 whitespace-nowrap shadow-xl pointer-events-none transition-opacity">
                                            <div className="font-bold">{new Date(d.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}</div>
                                            <div className="text-amber-400 font-extrabold">{d.count} discussions</div>
                                        </div>
                                    </div>
                                    <div className="text-[9px] text-gray-400 mt-3 font-medium rotate-45 origin-left whitespace-nowrap">
                                        {new Date(d.date).getDate()}/{new Date(d.date).getMonth() + 1}
                                    </div>
                                </div>
                            );
                        }) : (
                            <div className="flex-1 flex items-center justify-center text-gray-300 italic">Aucune donnée sur la période.</div>
                        )}
                    </div>
                </div>

                {/* Daily History Table */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                    <div className="p-6 border-b border-gray-100 bg-gray-50/50">
                        <h3 className="text-sm font-bold text-gray-800 uppercase tracking-widest flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse"></span>
                            Détail Journalier
                        </h3>
                    </div>
                    <div className="overflow-auto max-h-80">
                        <table className="w-full text-left text-xs text-gray-600">
                            <thead className="bg-gray-100/50 text-gray-500 sticky top-0 uppercase font-bold text-[10px]">
                                <tr>
                                    <th className="p-4 border-b border-gray-100">Date</th>
                                    <th className="p-4 border-b border-gray-100 text-right">Discussions Initiées</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {shopperStats?.daily30d && shopperStats.daily30d.length > 0 ? shopperStats.daily30d.slice().reverse().map((d, idx) => (
                                    <tr key={idx} className="hover:bg-amber-50/50 transition-colors">
                                        <td className="p-3 font-medium text-gray-900 dark:text-white">{new Date(d.date).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'long' })}</td>
                                        <td className="p-3 text-right font-black text-amber-600">{d.count}</td>
                                    </tr>
                                )) : (
                                    <tr><td colSpan="2" className="p-8 text-center text-gray-400">Aucune discussion enregistrée.</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        );
    };

    const menuItems = [
        { id: 'overview', label: 'Vue d\'ensemble', icon: '📊' },
        { id: 'shopper', label: 'Liya Shopper', icon: '🤖' },
        { id: 'audience', label: 'Audience', icon: '👥' },
        { id: 'clicks', label: 'Clics Sites', icon: '🔗' },
        { id: 'products', label: 'Produits', icon: '🛍️' },
        { id: 'brands', label: 'Marques', icon: '🏷️' },
        { id: 'engagement', label: 'Engagement', icon: '❤️' },
        { id: 'activity', label: 'Activité', icon: '📅' },
        { id: 'devices', label: 'Appareils', icon: '📱' },
        { id: 'categories', label: 'Collections', icon: '📂' },
    ];

    const handleResetStats = async () => {
        if (window.confirm("Êtes-vous sûr de vouloir supprimer TOUTES les statistiques ? Cette action est irréversible (l'historique, les likes, les vues seront effacés).")) {
            try {
                await apiClient.admin.resetStats();
                window.location.reload(); // Reload to refresh everything empty
            } catch (err) {
                alert("Erreur lors de la suppression : " + err.message);
            }
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col md:flex-row overflow-x-auto">
            {/* Sidebar Navigation */}
            <aside className="w-full md:w-64 md:flex-shrink-0 bg-white border-r border-gray-200 md:min-h-full z-10">
                <div className="p-6 border-b border-gray-100 flex justify-between items-center">
                    <h1 className="text-xl font-extrabold text-gray-900 dark:text-white">Admin Stats</h1>
                    <button title="Reset" onClick={handleResetStats} className="text-xs text-red-500 border border-red-200 px-2 py-1 rounded hover:bg-red-50">🗑️</button>
                </div>
                <nav className="p-4 space-y-1 max-h-[70vh] md:max-h-none overflow-y-auto">
                    {menuItems.map(item => (
                        <button
                            key={item.id}
                            onClick={() => setActiveTab(item.id)}
                            className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${activeTab === item.id
                                ? 'bg-amber-50 text-amber-800 dark:bg-amber-950/40 dark:text-amber-300'
                                : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900 dark:text-white'
                                }`}
                        >
                            <span>{item.icon}</span>
                            <span>{item.label}</span>
                        </button>
                    ))}
                </nav>
            </aside>

            {/* Main Content */}
            <main className="flex-1 min-w-0 p-4 md:p-10 overflow-x-auto">
                {/* Mobile Header Placeholder if needed */}
                <div className="max-w-6xl mx-auto space-y-8">
                    {activeTab === 'overview' && renderOverview()}
                    {activeTab === 'shopper' && renderShopper()}
                    {activeTab === 'audience' && renderAudience()}
                    {activeTab === 'engagement' && renderEngagement()}
                    {activeTab === 'activity' && renderActivity()}
                    {activeTab === 'devices' && renderDevices()}
                    {activeTab === 'categories' && renderCategories()}
                    {activeTab === 'products' && renderProducts()}
                    {activeTab === 'brands' && renderBrands()}
                    {activeTab === 'clicks' && renderClicks()}
                </div>
            </main>
        </div>
    );
};
// ... StatCard remains same

const StatCard = ({ title, value, subtitle, color = "blue" }) => {
    const colors = {
        blue: "border-amber-500 text-amber-600",
        purple: "border-amber-600 text-amber-700",
        green: "border-green-500 text-green-600",
        orange: "border-orange-500 text-orange-600",
        red: "border-red-500 text-red-600",
        pink: "border-amber-400 text-amber-500",
        indigo: "border-amber-500 text-amber-600"
    };

    const selectedColor = colors[color] || colors.blue;
    const [borderClass, textClass] = selectedColor.split(' ');

    return (
        <div className={`bg-white p-6 rounded-xl shadow-sm border-l-4 ${borderClass} hover:shadow-md transition-shadow`}>
            <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider">{title}</h3>
            <p className="text-4xl font-extrabold text-gray-900 dark:text-white mt-2">{value !== undefined ? value : '-'}</p>
            <div className={`mt-2 text-sm font-medium ${textClass} bg-opacity-10 py-1 px-2 rounded w-fit`}>
                {subtitle}
            </div>
        </div>
    );
};

export default Stats;
